import React, {useState, useEffect} from "react";

// MUI COMPONENTS
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import MuiButton from "@mui/material/Button";

// COMPONENTS
import ContentBox from "@components/ContentBox/ContentBox";

// HELPERS
import api from "@helpers/api";
import {createClassName} from "@helpers/utils";
import TextField from "../../../../../components/TextField/TextField.jsx";
import Validator from "../../../../../classes/Validator.js";
import i18n from "../../../../../helpers/i18n.js";
import ActivityIndicator from "../../../../../components/ActivityIndicator/ActivityIndicator.jsx";
import TextView from "../../../../../components/TextView/TextView.jsx";
import { getViewInitialStateForCustomer as GetViewInitialStateForCustomer_CustomerInformationFair } from "../../CustomerInformation/Fair/CustomerInformation";
import CustomerInformationFairFulfillment from "../../CustomerInformation/Fair/CustomerInformation.fulfillment";

const SectionSummaryFull = props =>
{
	const [loaded, setLoaded] = useState(false);
	const [customerEmail, setCustomerEmail] = useState(props.customer.getData("customer_email", ""));
	const [downloading, setDownloading] = useState(false);
	const [sending, setSending] = useState(false);
	const [summarySent, setSummarySent] = useState(false);
	const [summaryEmail, setSummaryEmail] = useState("");
	const [summaryEmailEdited, setSummaryEmailEdited] = useState(false);

	const downloadSummary = async () =>
	{
		if (downloading)
		{
			return false;
		}

		setDownloading(true);
		console.log("Downloading summary...");

		const rawDataInBase64 = await api("/case/downloadSummary",
			{
				caseId,
				template: isCompany ? "delsummering/company.pdf" : "delsummering/person.pdf",
				frontpage: "frontpage.pdf",
				attachment: null
			},
			{responseType: "arraybuffer"},
			true
		);

		if (rawDataInBase64 !== "")
		{
			const file = new Blob([rawDataInBase64], {type: "application/pdf"});
			const fileURL = URL.createObjectURL(file);
			const link = document.createElement("a");

			let today = new Date();
			link.href = fileURL;
			link.download = "Delsummering " + today.toISOString().split('T')[0] + ".pdf";
			link.click();
		}
		else
		{
			console.log("Could not download case data");
		}
		setDownloading(false);
	};

	const sendSummary = async () =>
	{
		if (sending)
		{
			return false;
		}

		setSending(true);
		setSummarySent(false);
		console.log("Sending summary...");

		await api("/case/downloadSummary",
			{
				caseId,
				template: isCompany ? "delsummering/company.pdf" : "delsummering/person.pdf",
				frontpage: "frontpage.pdf",
				attachment: null,
				sendToEmail: customerEmail,
				emailBody: summaryEmail.length === 0 ? getDefaultText() : summaryEmail
			},
			{responseType: "arraybuffer"},
			true
		);

		setSending(false);
		setSummarySent(true);
	};

	const getDefaultText = () => {
		const {customer} = props;

		let greeting = "Hej\n\n";
		let name = customer.getData("customer_firstname", "");
		if (name.length > 0)
		{
			greeting = "Hej " + name + ",\n\n";
		}

		const text = "Tack för trevligt möte. Bifogat finner du en mötessammanfattning, hör gärna av dig om du har " +
			"frågor eller funderingar.\n\n" +
			"Önskar dig en fortsatt fin dag så ses vi på vårt uppföljningsmöte.\n\n"

		const signoff = "Bästa hälsningar,\n" + customer.getData("partner_fullname", "Fair Investments AB")

		return greeting + text + signoff;
	}

	useEffect(() =>
	{
		const {setAppState} = props;

		if (!loaded)
		{
			setAppState({
				viewFulfilled: true,
				showValidationWarning: false
			});
			setLoaded(true);
		}
	}, [props]);

	const {customer} = props;
	const {caseId, isCompany} = customer;
	const classes = createClassName("Cob-view", {});

	// To prevent emailing a summary that is incomplete to a customer, we check that the CustomerInformation view
	// has been completed before enabling the email form and button.
	const detailsState = GetViewInitialStateForCustomer_CustomerInformationFair(customer);
	const detailsFulfilled = CustomerInformationFairFulfillment(detailsState);

	let emailBodyForDisplay = "";
	if (summaryEmail && summaryEmail.length > 0)
	{
		emailBodyForDisplay = summaryEmail;
	}
	else if (!summaryEmailEdited)
	{
		emailBodyForDisplay = getDefaultText();
	}

	return (
		<div className={classes}>
			<ContentBox>
				<h2>Delsummering</h2>

				<Box sx={{
					width: "100%",
					display: "flex",
					flexDirection: "column",
					justifyContent: "flex-start",
					alignItems: "flex-start",
					mb: 2
				}}>
					<Box sx={{mb: 2}}>
						<Typography variant="subtitle1" color="primary.dark">
							Ladda ner en PDF med sammanfattning av alla besvarade frågor hittills för denna session.
						</Typography>
					</Box>
					<MuiButton
						id="fade-button"
						variant="contained"
						onClick={downloadSummary}
						sx={{
							backgroundColor: "button.main",
							flexDirection: "row",
							boxShadow: "none",
							height: "32px",
							width: "140px",
							justifyContent: "center",
							"&:hover": {
								backgroundColor: "button.main",
								filter: "brightness(105%)",
								boxShadow: "none"
							}
						}}>
						{downloading ?
							(
								<ActivityIndicator scale={0.4} busy/>
							)
							:
							(
								<Typography
									variant="button"
									sx={{
										fontSize: "12px",
										fontWeight: 400,
										textTransform: "uppercase",
										textDecoration: "none"
									}}>
									Ladda ner PDF
								</Typography>
							)
						}
					</MuiButton>
				</Box>

				<Box sx={{
					width: "100%",
					display: "flex",
					flexDirection: "column",
					justifyContent: "flex-start",
					alignItems: "flex-start",
					mb: 2,
					borderTop: "1px dashed #DDD",
					paddingTop: "30px",
					marginTop: "20px"
				}}>
					<Box sx={{mb: 2}}>
						<Typography variant="subtitle1" color="primary.dark">
							Du kan även skicka sammanfattningen direkt till kund med e-post.
						</Typography>
					</Box>
					<TextField
						type="email"
						name="customer_dont_autocomplete_19"
						value={customerEmail}
						autocomplete="off"
						label={i18n("general", "email_address")}
						validation={Validator.validateEmail}
						onChange={({target}) => setCustomerEmail(target.value)}
						disabled={!detailsFulfilled}
					/>

					<TextView
						label={`Meddelande`}
						value={emailBodyForDisplay}
						maxLength={1800}
						validation={value => value.length > 0}
						resize="vertical"
						onChange={({target}) => {
							setSummaryEmail(target.value);
							setSummaryEmailEdited(true);
						}}
						fullWidth
						minHeight={190}
						style={{marginTop: "10px"}}
						disabled={!detailsFulfilled}
					/>

					<MuiButton
						id="fade-button"
						variant="contained"
						onClick={sendSummary}
						disabled={
							!detailsFulfilled ||
							!Validator.validateEmail(customerEmail) ||
							(summaryEmailEdited && summaryEmail.length <= 0)
					    }
						sx={{
							backgroundColor: "button.main",
							flexDirection: "row",
							justifyContent: "center",
							boxShadow: "none",
							height: "32px",
							width: "170px",
							marginTop: "15px",
							"&:hover": {
								backgroundColor: "button.main",
								filter: "brightness(105%)",
								boxShadow: "none"
							}
						}}>
						{sending ?
							(
								<ActivityIndicator scale={0.4} busy/>
							)
							:
							(
								<Typography
									variant="button"
									sx={{
										fontSize: "12px",
										fontWeight: 400,
										textTransform: "uppercase",
										textDecoration: "none"
									}}>
									Skicka som e-post
								</Typography>
							)
						}
					</MuiButton>

					{summarySent && (
						<Typography
							variant="button"
							sx={{
								fontSize: "13px",
								fontWeight: 400,
								textDecoration: "none",
								marginTop: "10px"
							}}>
							Mötessammanfattningen skickades.
						</Typography>
					)}

					{!detailsFulfilled && (
						<p style={{color: "#df4c43"}}>
							Avsnittet "Grunduppgifter" måste fyllas i helt innan sammanfattningen kan mailas ut.
						</p>
					)}
				</Box>
			</ContentBox>
		</div>
	);
};

export default SectionSummaryFull;